import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { flowAnswers, flowProgress, flowReducer, flowUnavailable, sheetView, startFlow } from './flow';
import type { FlowAction, FlowState, FlowTraveler } from './flow';
import { hydrateFlowDraft, readFlowDraft, saveFlowDraft } from './draft';
import { loadFlowLists } from './lists';
import { todayIso } from './dates';
import type { FlowLists } from './types';

type Holder = { state: FlowState | null };
type HolderAction = { type: 'set'; state: FlowState } | FlowAction;

function holderReducer(holder: Holder, action: HolderAction): Holder {
  if (action.type === 'set') return { state: action.state };
  return holder.state ? { state: flowReducer(holder.state, action) } : holder;
}

export interface UseAlmaFlowOptions {
  /** Load lists and start (or resume) the flow. Off while the flag is off or the intro is showing. */
  enabled: boolean;
  persona: string;
  traveler: FlowTraveler;
}

/**
 * Phase 1 of Alma's two-phase /try chat: loads the admin-managed lists once,
 * starts a fresh flow or resumes the saved draft, and saves every change.
 * Rendering and the chat transcript stay in TryChat, which drains `outbox`
 * (acking each message) and renders the sheet from `view`.
 */
export function useAlmaFlow({ enabled, persona, traveler }: UseAlmaFlowOptions) {
  const [{ state }, dispatch] = useReducer(holderReducer, { state: null });
  const [lists, setLists] = useState<FlowLists | null>(null);
  const [loading, setLoading] = useState(false);
  const startedRef = useRef(false);
  const travelerRef = useRef(traveler);
  travelerRef.current = traveler;

  const load = useCallback(async () => {
    setLoading(true);
    const loaded = await loadFlowLists();
    setLists(loaded);
    setLoading(false);
    return loaded;
  }, []);

  // Start or resume once, as soon as the flow is enabled.
  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;
    void load().then((loaded) => {
      const draft = readFlowDraft();
      if (draft) {
        dispatch({ type: 'set', state: hydrateFlowDraft(draft, loaded) });
        dispatch({ type: 'lists', lists: loaded });
      } else {
        dispatch({ type: 'set', state: startFlow(loaded, persona, travelerRef.current, Date.now()) });
      }
    });
  }, [enabled, load, persona]);

  useEffect(() => {
    if (state) saveFlowDraft(state);
  }, [state]);

  /** Reloads every list after a failure; the current step re-renders or is skipped if now empty. */
  const retry = useCallback(async () => {
    const loaded = await load();
    dispatch({ type: 'lists', lists: loaded });
  }, [load]);

  const today = todayIso();
  return {
    state,
    /** Lists loaded, before the flow state exists. */
    lists,
    loading,
    /** No cities to offer — fall back to the classic chat. */
    unavailable: lists ? flowUnavailable(lists) : false,
    view: state ? sheetView(state, today) : null,
    progress: state ? flowProgress(state) : null,
    answers: state ? flowAnswers(state) : null,
    today,
    dispatch: dispatch as (action: FlowAction) => void,
    retry,
  };
}
